import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';
import { PDFFilePage } from '@/modules/pdf/types';

// Zoom constraints matching PDFZoomControls
const MIN_AUTO_ZOOM = 1.5;
const MAX_AUTO_ZOOM = 2.5;

// Default page dimensions (US Letter) when file metadata is unavailable
const DEFAULT_PAGE_WIDTH_INCHES = 8.5;
const DEFAULT_PAGE_HEIGHT_INCHES = 11;

// The fraction of the viewport width we want the annotation to fill.
// 0.6 means the annotation should span roughly 60% of the visible area.
const TARGET_WIDTH_FRACTION = 0.6;

// The fraction of the viewport height we want the annotation to occupy at most.
// This prevents over-zooming on annotations that are very narrow but tall.
const TARGET_HEIGHT_FRACTION = 0.5;

/**
 * Compute a zoom level that makes the focused annotation comfortably readable.
 *
 * The calculation is based on how much of the page the annotation's bounding
 * box covers.  At zoom 1.0 the full page width fills the container, so a
 * small annotation will be hard to read.  We compute the zoom that would make
 * the annotation fill TARGET_WIDTH_FRACTION of the viewport, while also
 * ensuring it doesn't exceed TARGET_HEIGHT_FRACTION vertically.
 *
 * When `filePages` are provided, actual page dimensions are used for the
 * annotation's page; otherwise we fall back to US Letter defaults.
 *
 * The result is clamped to [MIN_AUTO_ZOOM, MAX_AUTO_ZOOM].
 */
export function computeAutoZoomLevel(
  annotations: DocumentSearchResult[],
  activeAnnotationIndex: number,
  filePages?: PDFFilePage[]
): number {
  const annotation = annotations[activeAnnotationIndex] ?? annotations[0];
  if (!annotation?.Polygon?.length) {
    // No geometry available — use a reasonable default
    return MIN_AUTO_ZOOM;
  }

  // Use actual page dimensions when available, falling back to US Letter defaults.
  // annotation.PageNumber is 1-indexed.
  const pageIndex = (annotation.PageNumber ?? 1) - 1;
  const page = filePages?.[pageIndex];
  const pageWidth = page?.widthInches ?? DEFAULT_PAGE_WIDTH_INCHES;
  const pageHeight = page?.heightInches ?? DEFAULT_PAGE_HEIGHT_INCHES;

  // Compute annotation bounding box extents (coordinates are in inches)
  const xs = annotation.Polygon.map((p) => p.x);
  const ys = annotation.Polygon.map((p) => p.y);

  const annotWidth = Math.max(...xs) - Math.min(...xs);
  const annotHeight = Math.max(...ys) - Math.min(...ys);

  // Express annotation dimensions as fractions of the page
  const widthFraction = annotWidth / pageWidth;
  const heightFraction = annotHeight / pageHeight;

  // Avoid division by zero for degenerate annotations
  const safeWidthFraction = Math.max(widthFraction, 0.01);
  const safeHeightFraction = Math.max(heightFraction, 0.01);

  // Zoom needed to make the annotation fill the target fractions
  const zoomFromWidth = TARGET_WIDTH_FRACTION / safeWidthFraction;
  const zoomFromHeight = TARGET_HEIGHT_FRACTION / safeHeightFraction;

  // Use the more conservative (smaller) zoom so the annotation fits both ways
  const computedZoom = Math.min(zoomFromWidth, zoomFromHeight);

  return Math.min(Math.max(computedZoom, MIN_AUTO_ZOOM), MAX_AUTO_ZOOM);
}
